import { useMemo } from "react"
import { Skeleton, Typography } from "@mui/material"
import CardContent from "@mui/material/CardContent"
import Stack from "@mui/material/Stack"
import Divider from "@mui/material/Divider"
import fp from "lodash/fp"
import { useTranslation } from "react-i18next"

import GeoJSONLayers from "widgets/plots/geo/GeoJSONLayers"
import useRoutes from "helpers/hooks/useRoutes"
import { Card } from "widgets/styled/containers"
import CustomLink from "widgets/common/CustomLink"
import type { Powerline } from "types/powerlines.types"
import { featureProperties, getMapCenterFromGeo } from "helpers/utils/map"
import { formatDecimalValues } from "helpers/utils/common"
import { formatCurrentSeasonalAmpacity } from "helpers/utils/seasonalAmpacity"
import { nameOrUnnamed } from "helpers/utils/translations"
import usePowerlineGeo from "helpers/hooks/powerlines/usePowerlineGeo"
import Map from "widgets/plots/geo/Map"
import { CARD_MAP_HEIGHT } from "helpers/utils/constants"
import { customValueFormatter } from "helpers/formatters/plots/plots"

interface PowerlineCardProps {
  powerline: Powerline
}

const mapLayersVisibility = {
  PowerlineSpan: false,
  PowerlineTower: false,
  Device: false,
}

const PowerlineCard = ({ powerline }: PowerlineCardProps) => {
  const { baseOrgURL } = useRoutes()
  const { powerlineGeoData, isLoading } = usePowerlineGeo({
    id: powerline.id,
  })
  const { i18n, t } = useTranslation()
  const locale = i18n.resolvedLanguage ?? "en"

  const center = useMemo(() => getMapCenterFromGeo(powerlineGeoData), [powerlineGeoData])

  const seasonal = formatCurrentSeasonalAmpacity(powerline.ampacity_seasonal, locale)

  return (
    <>
      <Card
        id="pwCard"
        elevation={0}
        sx={{
          backgroundColor: (theme) => theme.palette.background.paper,
          minHeight: "450px",
          height: "100%",
          border: (theme) => `1px solid ${theme.palette.neutral[400]}`,
          padding: 0,
          display: "flex",
        }}
      >
        <CardContent
          sx={{
            width: "100%",
            p: "1rem",
          }}
        >
          <Stack spacing={2} justifyContent="space-between" height="100%">
            <Stack spacing={1} divider={<Divider flexItem />}>
              <CustomLink
                variant="h6"
                href={`${baseOrgURL}/powerlines/${powerline.id}`}
                hover
                bold
                styles={{ fontStyle: powerline.name ? "normal" : "italic" }}
              >
                {nameOrUnnamed(powerline.name, t)}
              </CustomLink>

              {featureProperties.Powerline.map((property: string, i) => {
                // Seasonal ampacity replaces the (deprecated) nominal ampacity slot,
                // showing the value in effect today and its date window.
                if (property === "ampacity_nominal" && seasonal) {
                  return (
                    <Stack
                      flexDirection="row"
                      justifyContent="space-between"
                      key={`PowerlineProperty-${powerline.id}-${i}`}
                    >
                      <Typography
                        variant="body1"
                        sx={{ color: (theme) => theme.palette.text.secondary }}
                      >
                        {t("powerlines.AMPACITY_NOMINAL")}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: (theme) => theme.palette.text.secondary }}
                      >
                        {seasonal.label}
                      </Typography>
                    </Stack>
                  )
                }
                const propertyValue = powerline[property as keyof Powerline]
                if (!fp.isNil(propertyValue)) {
                  // Object-valued fields (e.g. ampacity_seasonal) are not displayed here.
                  if (
                    typeof propertyValue === "object" &&
                    !Array.isArray(propertyValue)
                  ) {
                    return null
                  }
                  const formattedProperty = () => {
                    let result = propertyValue
                    if (typeof propertyValue === "number") {
                      if (property === "voltage_nominal") {
                        result = customValueFormatter({
                          suffix: "V",
                          decimalPlaces: 0,
                          formatUnits: true,
                          locale,
                        })(propertyValue)
                      } else if (property === "ampacity_nominal") {
                        result = customValueFormatter({
                          suffix: "A",
                          decimalPlaces: 0,
                          formatUnits: false,
                          locale,
                        })(propertyValue)
                      } else if (property === "length") {
                        result = customValueFormatter({
                          suffix: "m",
                          decimalPlaces: 2,
                          formatUnits: true,
                          locale,
                        })(propertyValue)
                      } else {
                        result = formatDecimalValues(result as number, locale)
                      }
                    }
                    return result
                  }
                  return (
                    <Stack
                      flexDirection="row"
                      justifyContent="space-between"
                      key={`PowerlineProperty-${powerline.id}-${i}`}
                    >
                      <Typography
                        variant="body1"
                        sx={{ color: (theme) => theme.palette.text.secondary }}
                      >
                        {t(`powerlines.${property.toUpperCase()}`)}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: (theme) => theme.palette.text.secondary }}
                      >
                        {formattedProperty()}
                      </Typography>
                    </Stack>
                  )
                }
              })}
            </Stack>
            {isLoading ? (
              <Skeleton variant="rounded" height={CARD_MAP_HEIGHT} />
            ) : (
              <Map center={center} mapHeight={CARD_MAP_HEIGHT}>
                {/* Only shows where the line runs. The card above has its data, and
                    its page has the full map. */}
                <GeoJSONLayers
                  data={powerlineGeoData}
                  layersVisibility={mapLayersVisibility}
                  showLayersControl={false}
                  showPopups={false}
                />
              </Map>
            )}
          </Stack>
        </CardContent>
      </Card>
    </>
  )
}

export default PowerlineCard
